import { isMeaningfulInsurancePlanName } from "@/src/lib/insurance/match-valerie-insurance-plan";
export { isMeaningfulInsurancePlanName } from "@/src/lib/insurance/match-valerie-insurance-plan";
import type { ConfirmedAthenaInsurancePlan } from "@/src/models/athena-insurance-plan";
import type { Coverage } from "@/src/models/coverage";

function normalizeInsuranceIdentity(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function resolveUniqueKeyMatch(
  plans: readonly ConfirmedAthenaInsurancePlan[],
  key: string | null
): ConfirmedAthenaInsurancePlan | null {
  if (!key) return null;

  const matches = plans.filter((plan) => plan.verifiedMatchKeys.includes(key));

  return matches.length === 1 ? matches[0]! : null;
}

export function buildAthenaInsurancePlanMatchKey(
  payer: string | null | undefined,
  plan: string | null | undefined
): string | null {
  const normalizedPayer = normalizeInsuranceIdentity(payer);
  const normalizedPlan = normalizeInsuranceIdentity(plan);

  return normalizedPayer && normalizedPlan ? `payer-plan:${normalizedPayer}::${normalizedPlan}` : null;
}

export function resolveConfirmedAthenaInsurancePlan(
  plans: readonly ConfirmedAthenaInsurancePlan[],
  coverage: Coverage,
  eligibilityPayerName: string | null,
  eligibilityProductText: string | null,
  eligibilityPlanName: string | null
): ConfirmedAthenaInsurancePlan | null {
  if (coverage.valerieInsurancePlanId && plans.some((plan) => plan.affiliatedValerieInsurancePlanIds !== undefined)) {
    const matches = plans.filter((plan) =>
      plan.affiliatedValerieInsurancePlanIds?.includes(coverage.valerieInsurancePlanId!)
    );

    // Once the canonical plan is known, a name fallback must not bypass a
    // missing or ambiguous affiliation.
    return matches.length === 1 ? matches[0]! : null;
  }

  const eligibilityKey = buildAthenaInsurancePlanMatchKey(eligibilityPayerName, eligibilityProductText);

  if (eligibilityKey) {
    const eligibilityMatch = resolveUniqueKeyMatch(plans, eligibilityKey);

    if (eligibilityMatch || isMeaningfulInsurancePlanName(eligibilityPlanName)) return eligibilityMatch;
  }

  return resolveUniqueKeyMatch(plans, buildAthenaInsurancePlanMatchKey(coverage.payer, coverage.plan));
}
